import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiLightBulb, HiRefresh, HiExclamation } from 'react-icons/hi';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';
import { formatDateInput, defaultRange } from '../components/PeriodDateRangeModal';

const PRESETS = [
  { days: 7, label: '7 дней' },
  { days: 14, label: '14 дней' },
  { days: 30, label: '30 дней' },
];

const ACTION_LABELS = {
  launch: 'Запуск',
  scale: 'Усиление',
  cut: 'Пауза / урезание',
  restock: 'Пополнить остаток',
  fix_card: 'Сначала карточка',
  budget: 'Бюджет',
};

const PRIORITY_ORDER = { P0: 0, P1: 1, P2: 2 };

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
}

function num(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('ru-RU');
}

function PriorityBadge({ priority }) {
  const cls =
    priority === 'P0'
      ? 'bg-rose-100 text-rose-800 border-rose-200'
      : priority === 'P1'
        ? 'bg-amber-100 text-amber-900 border-amber-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-md border ${cls}`}>
      {priority || '—'}
    </span>
  );
}

function ActionBadge({ category }) {
  const tones = {
    launch: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    scale: 'text-blue-700 bg-blue-50 border-blue-200',
    cut: 'text-rose-700 bg-rose-50 border-rose-200',
    restock: 'text-amber-800 bg-amber-50 border-amber-200',
    fix_card: 'text-violet-700 bg-violet-50 border-violet-200',
    budget: 'text-slate-700 bg-slate-50 border-slate-200',
  };
  return (
    <span className={`inline-flex text-xs px-2 py-0.5 rounded-md border whitespace-nowrap ${tones[category] || tones.budget}`}>
      {ACTION_LABELS[category] || category || '—'}
    </span>
  );
}

function buildSummaryRows(result) {
  if (!result) return [];

  const byNm = new Map();
  Object.values(result.portfolio || {}).forEach((list) => {
    (list || []).forEach((item) => {
      if (item?.nmId != null) byNm.set(Number(item.nmId), item);
    });
  });
  (result.topByScore || []).forEach((item) => {
    if (item?.nmId != null && !byNm.has(Number(item.nmId))) byNm.set(Number(item.nmId), item);
  });

  const reallocByNm = new Map();
  (result.budgetReallocation || []).forEach((b) => {
    if (b.toNmId != null) {
      const cur = reallocByNm.get(Number(b.toNmId)) || [];
      cur.push(b);
      reallocByNm.set(Number(b.toNmId), cur);
    }
    if (b.fromNmId != null) {
      const cur = reallocByNm.get(Number(b.fromNmId)) || [];
      cur.push(b);
      reallocByNm.set(Number(b.fromNmId), cur);
    }
  });

  const seen = new Set();
  const rows = [];

  (result.priorityActions || []).forEach((a) => {
    const nmId = a.nmId != null ? Number(a.nmId) : null;
    if (nmId != null) seen.add(nmId);
    const m = nmId != null ? byNm.get(nmId) : null;
    const realloc = nmId != null ? reallocByNm.get(nmId) : null;
    rows.push({
      key: a.id || `action-${nmId}-${a.category}`,
      priority: a.priority || 'P2',
      category: a.category,
      nmId,
      vendorCode: a.vendorCode || m?.vendorCode || '',
      productName: m?.productName || '',
      score: m?.score ?? null,
      adSpend: m?.adSpend ?? null,
      adOrders: m?.adOrders ?? null,
      cpo: m?.cpo ?? null,
      stock: m?.stock ?? null,
      coverageDays: m?.coverageDays ?? null,
      targetStock: a.targetStock ?? m?.targetStock ?? null,
      weeklyBudget: a.weeklyBudgetHintRub ?? m?.weeklyBudgetHint ?? null,
      action: a.action || a.title || '',
      cause: a.cause || '',
      confidence: a.confidence || '',
      reallocText: realloc
        ? realloc
            .map((b) => `${money(b.amountRubPerWeek)}: ${b.fromVendorCode || b.fromNmId || '—'} → ${b.toVendorCode || b.toNmId}`)
            .join('; ')
        : '',
    });
  });

  // Портфельные позиции без отдельного priorityAction — в хвост таблицы
  const basketPriority = { cut: 'P0', restock: 'P1', scale: 'P1', launch: 'P1', fix_card: 'P2' };
  ['cut', 'restock', 'scale', 'launch', 'fix_card'].forEach((basket) => {
    (result.portfolio?.[basket] || []).forEach((m) => {
      const nmId = Number(m.nmId);
      if (seen.has(nmId)) return;
      seen.add(nmId);
      rows.push({
        key: `pf-${basket}-${nmId}`,
        priority: basketPriority[basket] || 'P2',
        category: basket,
        nmId,
        vendorCode: m.vendorCode || '',
        productName: m.productName || '',
        score: m.score,
        adSpend: m.adSpend,
        adOrders: m.adOrders,
        cpo: m.cpo,
        stock: m.stock,
        coverageDays: m.coverageDays,
        targetStock: m.targetStock,
        weeklyBudget: m.weeklyBudgetHint,
        action: ACTION_LABELS[basket] || basket,
        cause: '',
        confidence: '',
        reallocText: '',
      });
    });
  });

  rows.sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority] ?? 9;
    const pb = PRIORITY_ORDER[b.priority] ?? 9;
    if (pa !== pb) return pa - pb;
    return (b.score || 0) - (a.score || 0);
  });

  return rows;
}

export default function RecommendationsPage({ clientId }) {
  const { notify } = useNotification();
  const initial = defaultRange(13);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  const loadPlan = useCallback(async () => {
    if (!clientId) return;
    setLoadingPlan(true);
    try {
      const { data } = await apiClient.get('/recommendations/plan', {
        params: { client_id: clientId },
      });
      setPlan(data.plan || null);
    } catch (err) {
      notify(err.response?.data?.message || 'Не удалось загрузить план бюджета', 'error');
    } finally {
      setLoadingPlan(false);
    }
  }, [clientId, notify]);

  const loadLatest = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await apiClient.get('/recommendations/latest', {
        params: { client_id: clientId },
      });
      if (data?.latest?.recommendations) {
        setResult(data.latest.recommendations);
        if (data.latest.dateFrom) setDateFrom(formatDateInput(data.latest.dateFrom));
        if (data.latest.dateTo) setDateTo(formatDateInput(data.latest.dateTo));
      }
    } catch (_) {
      /* нет прошлых — ок */
    }
  }, [clientId]);

  useEffect(() => {
    loadPlan();
    loadLatest();
  }, [loadPlan, loadLatest]);

  const applyPreset = (days) => {
    const range = defaultRange(days - 1);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const handleGenerate = async () => {
    if (!clientId) return;
    setGenerating(true);
    try {
      const { data } = await apiClient.post('/recommendations/generate', {
        client_id: clientId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setResult(data.recommendations);
      if (data.recommendations?.plan) setPlan(data.recommendations.plan);
      notify('Рекомендации сформированы', 'success');
      setFilterPriority('all');
      setFilterAction('all');
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка формирования', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const allRows = useMemo(() => buildSummaryRows(result), [result]);

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (filterPriority !== 'all' && r.priority !== filterPriority) return false;
      if (filterAction !== 'all' && r.category !== filterAction) return false;
      return true;
    });
  }, [allRows, filterPriority, filterAction]);

  const counts = useMemo(() => {
    const byPriority = { P0: 0, P1: 0, P2: 0 };
    const byAction = {};
    allRows.forEach((r) => {
      if (byPriority[r.priority] != null) byPriority[r.priority] += 1;
      byAction[r.category] = (byAction[r.category] || 0) + 1;
    });
    return { byPriority, byAction };
  }, [allRows]);

  return (
    <div className="space-y-5">
      <div className="wb-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <HiLightBulb className="w-5 h-5 text-[var(--wb-primary)]" />
              Рекомендации по портфелю
            </h2>
            <p className="text-sm text-[var(--wb-text-muted)] mt-1">
              Лимит и KPI — из месячного бюджета кабинета. Выберите период и сформируйте сводную таблицу.
            </p>
          </div>
          <button type="button" className="wb-btn-ghost text-sm" onClick={loadPlan} disabled={loadingPlan}>
            <HiRefresh className={`w-4 h-4 inline mr-1 ${loadingPlan ? 'animate-spin' : ''}`} />
            Обновить план
          </button>
        </div>

        {plan && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-[var(--wb-text-muted)] border-b border-[var(--wb-border)]">
                  <th className="py-2 pr-3">Месяц</th>
                  <th className="py-2 pr-3">Лимит</th>
                  <th className="py-2 pr-3">Потрачено</th>
                  <th className="py-2 pr-3">Остаток</th>
                  <th className="py-2 pr-3">
                    Цель {plan.optimizationMetric === 'cpo' ? 'CPO' : 'ACOS'}
                  </th>
                  <th className="py-2">Дневные лимиты</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 pr-3 font-medium">{plan.month}</td>
                  <td className="py-2 pr-3">{money(plan.totalBudget)}</td>
                  <td className="py-2 pr-3">{money(plan.spentBudget)}</td>
                  <td className="py-2 pr-3">{money(plan.remainingBudget)}</td>
                  <td className="py-2 pr-3">
                    {plan.optimizationMetric === 'cpo' ? money(plan.targetCpo) : `${plan.targetAcos}%`}
                  </td>
                  <td className="py-2">
                    {money(plan.minDailyLimit)} – {money(plan.maxDailyLimit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {plan?.note && (
          <div className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <HiExclamation className="w-5 h-5 shrink-0" />
            <span>{plan.note}</span>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-[var(--wb-border)]">
          <div className="flex gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                className="wb-btn-secondary text-sm"
                onClick={() => applyPreset(p.days)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs text-[var(--wb-text-muted)] mb-1">С</label>
            <input
              type="date"
              className="wb-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--wb-text-muted)] mb-1">По</label>
            <input
              type="date"
              className="wb-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="wb-btn"
            disabled={generating || !clientId}
            onClick={handleGenerate}
          >
            {generating ? 'Формируем…' : 'Сформировать рекомендации'}
          </button>
        </div>
      </div>

      {result && (
        <div className="wb-card p-5 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold">Сводная таблица</h2>
              <p className="text-sm text-[var(--wb-text-secondary)] mt-1 max-w-3xl">
                {result.diagnosis?.headline}
              </p>
              {(result.executiveSummary || []).length > 0 && (
                <p className="text-xs text-[var(--wb-text-muted)] mt-1">
                  {(result.executiveSummary || []).join(' · ')}
                </p>
              )}
            </div>
            <p className="text-xs text-[var(--wb-text-muted)] whitespace-nowrap">
              {result.period?.from} — {result.period?.to}
              {result.generatedAt
                ? ` · ${new Date(result.generatedAt).toLocaleString('ru-RU')}`
                : ''}
            </p>
          </div>

          {(result.dataGaps || []).length > 0 && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Пробелы: {(result.dataGaps || []).join('; ')}
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-[var(--wb-text-muted)] mr-1">Приоритет:</span>
            {['all', 'P0', 'P1', 'P2'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setFilterPriority(p)}
                className={`text-xs px-2.5 py-1 rounded-md border ${
                  filterPriority === p
                    ? 'bg-[var(--wb-surface-muted)] border-[var(--wb-border)] font-medium'
                    : 'bg-white border-[var(--wb-border)] text-[var(--wb-text-muted)]'
                }`}
              >
                {p === 'all' ? `Все (${allRows.length})` : `${p} (${counts.byPriority[p] || 0})`}
              </button>
            ))}
            <span className="text-xs text-[var(--wb-text-muted)] ml-2 mr-1">Действие:</span>
            <button
              type="button"
              onClick={() => setFilterAction('all')}
              className={`text-xs px-2.5 py-1 rounded-md border ${
                filterAction === 'all'
                  ? 'bg-[var(--wb-surface-muted)] border-[var(--wb-border)] font-medium'
                  : 'bg-white border-[var(--wb-border)] text-[var(--wb-text-muted)]'
              }`}
            >
              Все
            </button>
            {Object.keys(ACTION_LABELS).filter((k) => counts.byAction[k]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilterAction(k)}
                className={`text-xs px-2.5 py-1 rounded-md border ${
                  filterAction === k
                    ? 'bg-[var(--wb-surface-muted)] border-[var(--wb-border)] font-medium'
                    : 'bg-white border-[var(--wb-border)] text-[var(--wb-text-muted)]'
                }`}
              >
                {ACTION_LABELS[k]} ({counts.byAction[k]})
              </button>
            ))}
          </div>

          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="text-left text-xs text-[var(--wb-text-muted)] border-b border-[var(--wb-border)]">
                  <th className="py-2 pr-2 sticky left-0 bg-[var(--wb-surface)] z-[1]">Приоритет</th>
                  <th className="py-2 pr-2">Действие</th>
                  <th className="py-2 pr-2">Артикул</th>
                  <th className="py-2 pr-2">Score</th>
                  <th className="py-2 pr-2">Расход</th>
                  <th className="py-2 pr-2">Заказы</th>
                  <th className="py-2 pr-2">CPO</th>
                  <th className="py-2 pr-2">Остаток</th>
                  <th className="py-2 pr-2">Покрытие</th>
                  <th className="py-2 pr-2">Цель остатка</th>
                  <th className="py-2 pr-2">Бюджет/нед</th>
                  <th className="py-2 pr-2 min-w-[280px]">Рекомендация</th>
                  <th className="py-2 min-w-[160px]">Перелив</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-[var(--wb-border)]/60 align-top hover:bg-[var(--wb-surface-muted)]/40">
                    <td className="py-2.5 pr-2 sticky left-0 bg-[var(--wb-surface)] z-[1]">
                      <PriorityBadge priority={r.priority} />
                    </td>
                    <td className="py-2.5 pr-2">
                      <ActionBadge category={r.category} />
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="font-medium whitespace-nowrap">{r.vendorCode || r.nmId || '—'}</div>
                      <div className="text-xs text-[var(--wb-text-muted)]">nm {r.nmId ?? '—'}</div>
                    </td>
                    <td className="py-2.5 pr-2">{r.score ?? '—'}</td>
                    <td className="py-2.5 pr-2 whitespace-nowrap">{money(r.adSpend)}</td>
                    <td className="py-2.5 pr-2">{num(r.adOrders)}</td>
                    <td className="py-2.5 pr-2 whitespace-nowrap">{r.cpo != null ? money(r.cpo) : '—'}</td>
                    <td className="py-2.5 pr-2">{num(r.stock)}</td>
                    <td className="py-2.5 pr-2 whitespace-nowrap">
                      {r.coverageDays != null ? `${r.coverageDays}д` : '—'}
                    </td>
                    <td className="py-2.5 pr-2">{num(r.targetStock)}</td>
                    <td className="py-2.5 pr-2 whitespace-nowrap">{money(r.weeklyBudget)}</td>
                    <td className="py-2.5 pr-2 text-[var(--wb-text-secondary)]">
                      <div>{r.action}</div>
                      {r.cause && (
                        <div className="text-xs text-[var(--wb-text-muted)] mt-1">{r.cause}</div>
                      )}
                      {r.confidence && (
                        <div className="text-xs text-[var(--wb-text-muted)] mt-0.5">
                          уверенность: {r.confidence}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 text-xs text-[var(--wb-text-muted)]">
                      {r.reallocText || '—'}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={13} className="py-8 text-center text-[var(--wb-text-muted)]">
                      Нет строк по выбранному фильтру
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!result && !generating && (
        <div className="wb-card p-8 text-center text-[var(--wb-text-muted)]">
          Выберите период и нажмите «Сформировать рекомендации».
        </div>
      )}
    </div>
  );
}
